import React from 'react';

export const ProgressStepper = ({ currentStep, isAnalyzing, isLoading }) => {
  const steps = [
    { 
      id: 1, 
      name: 'Enter Information', 
      description: 'Website & financials',
      icon: '📝',
      color: 'from-blue-500 to-indigo-600'
    },
    { 
      id: 2, 
      name: 'Valuation Report', 
      description: 'Get your results',
      icon: '📊',
      color: 'from-emerald-500 to-teal-600'
    },
  ];

  // Determine which step is active based on state
  const getStepStatus = (stepId) => {
    if (stepId < currentStep) return 'completed';
    if (stepId === currentStep) {
      if (stepId === 1 && (isAnalyzing || isLoading)) return 'processing';
      return 'active';
    }
    return 'upcoming';
  };

  return (
    <div className="mb-4 lg:mb-6">
      <div className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Desktop version - simple vertical */}
        <div className="hidden lg:block">
          <div className="flex items-center justify-center space-x-3">
            {steps.map((step, index) => {
              const status = getStepStatus(step.id);
              const isProcessing = status === 'processing';
              
              return (
                <React.Fragment key={step.id}>
                  {/* Step indicator */}
                  <div className="flex items-center">
                    <div className={`
                      w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300
                      ${status === 'completed' ? 'bg-green-500' : 
                        status === 'active' || isProcessing ? 'bg-blue-600' :
                        'bg-gray-200'}
                      ${isProcessing ? 'animate-pulse' : ''}
                    `}>
                      {status === 'completed' ? (
                        <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        <span className="text-white font-semibold text-sm">{step.id}</span>
                      )}
                    </div>
                    
                    {/* Step label - inline */}
                    <div className="ml-3">
                      <div className={`text-sm font-medium ${
                        status === 'upcoming' ? 'text-gray-400' : 'text-gray-700'
                      }`}>
                        {step.name}
                      </div>
                      <div className={`text-xs ${
                        status === 'upcoming' ? 'text-gray-300' : 'text-gray-500'
                      }`}>
                        {step.description}
                      </div>
                    </div>
                  </div>
                  
                  {/* Connector line */}
                  {index < steps.length - 1 && (
                    <div className={`w-12 h-0.5 ${
                      status === 'completed' ? 'bg-green-500' : 'bg-gray-300'
                    }`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Tablet version (simplified) */}
        <div className="hidden sm:block lg:hidden">
          <div className="flex items-center justify-between relative">
            {/* Progress line */}
            <div className="absolute left-10 right-10 top-5 h-0.5 bg-gray-200">
              <div
                className="h-full bg-gradient-to-r from-blue-600 to-emerald-600 transition-all duration-700"
                style={{
                  width: currentStep === 1 ? '0%' : '100%',
                }}
              />
            </div>

            {steps.map((step) => {
              const status = getStepStatus(step.id);
              
              return (
                <div key={step.id} className="relative z-10 flex-1 flex items-center justify-center">
                  <div className="flex flex-col items-center">
                    {/* Step circle */}
                    <div
                      className={`
                      w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 shadow-lg
                      ${
                        status === 'completed'
                          ? 'bg-green-500 scale-110'
                          : status === 'processing'
                            ? 'bg-blue-600 scale-125 animate-pulse'
                            : status === 'active'
                              ? 'bg-blue-600 scale-110'
                              : 'bg-white border-2 border-gray-300'
                      }
                    `}
                    >
                      {status === 'completed' ? (
                        <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        <span className={`text-2xl ${status === 'processing' ? 'animate-bounce' : ''}`}>
                          {step.icon}
                        </span>
                      )}
                    </div>

                    {/* Step label */}
                    <div className="mt-3 text-center">
                      <div className={`text-sm font-semibold ${
                        status === 'upcoming' ? 'text-gray-500' : 'text-gray-900'
                      }`}>
                        {step.name}
                      </div>
                      <div className={`text-xs mt-0.5 ${
                        status === 'upcoming' ? 'text-gray-400' : 'text-gray-600'
                      }`}>
                        {step.description}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

// Mini progress stepper for mobile
export const MiniProgressStepper = ({ currentStep }) => {
  return (
    <div className="mb-10 sm:mb-12 lg:hidden">
      <div className="flex items-center justify-center space-x-2">
        {[1, 2].map((step) => (
          <div
            key={step}
            className={`h-2 rounded-full transition-all duration-300 ${
              step <= currentStep ? 'w-16 bg-amiva-primary' : 'w-2 bg-gray-300'
            }`}
          />
        ))}
      </div>
      <p className="text-center text-sm text-gray-600 mt-2">Step {currentStep} of 2</p>
    </div>
  );
};
